import React, { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'src/common';
import { useCreateMp, getUpdateCacheOnCreateMp, useLoadMp } from 'src/models/matchingProfiles/hooks';
import { ProfileType } from '@tendium/prom-types';
import CopyMpModal from './CopyMpModal/CopyMpModal';
import { updateQueryParams } from 'src/helpers';
import { OverviewQp } from 'src/models/procurements/types';
import { SidebarMode } from 'src/shared/InfoSidebar/types';
import { useOpenSidebar } from 'src/shared/InfoSidebar/hooks';
import styles from './index.module.scss';

interface FormState {
  name: string;
}

export const CopyMatchingProfile: FC = () => {
  const { t } = useTranslation();

  const [createMp] = useCreateMp();
  const openSidebar = useOpenSidebar();
  const { data: mp } = useLoadMp();

  const [modalVisible, setModalVisible] = useState(false);

  const initialValues = useMemo((): FormState => {
    return {
      name: `${t('Tenders.copyOf')} ${mp?.name}`
    };
  }, [mp?.name, t]);

  const onSubmit = useCallback(
    (values: FormState) => {
      const { name } = values;
      createMp({
        variables: {
          name,
          profileType: ProfileType.Matching,
          keywords: mp?.keywords?.map(keyword => ({
            value: keyword.value,
            filterLogic: keyword.filterLogic,
            highlightColor: keyword.highlightColor,
            searchLogic: keyword.searchLogic,
            searchType: keyword.searchType
          })),
          buyerOrgIds: mp?.buyerOrgIds,
          buyerSearchStrings: mp?.buyerSearchStrings,
          cpvCodes: mp?.cpvCodes,
          cpvCodesExact: mp?.cpvCodesExact,
          nutsCodes: mp?.nutsCodes,
          nutsCodesExact: mp?.nutsCodesExact,
          buyerTypes: mp?.buyerTypes,
          contractValue: { currency: mp?.contractValueCurrency, min: mp?.contractValueMin, max: mp?.contractValueMax },
          contractValueIncludeMissingValue: mp?.contractValueIncludeMissingValue
        },
        update: getUpdateCacheOnCreateMp()
      }).then(({ data }) => {
        const mpId = data?.createMatchingProfile.id;
        if (!!mpId) {
          setModalVisible(false);
          updateQueryParams({
            [OverviewQp.MatchingProfileId]: mpId
          });
          openSidebar({ id: mpId, mode: SidebarMode.MP_INFO });
        }
        setModalVisible(false);
      });
    },
    [
      createMp,
      mp?.keywords,
      mp?.buyerOrgIds,
      mp?.buyerSearchStrings,
      mp?.cpvCodes,
      mp?.cpvCodesExact,
      mp?.nutsCodes,
      mp?.nutsCodesExact,
      mp?.buyerTypes,
      mp?.contractValueCurrency,
      mp?.contractValueMin,
      mp?.contractValueMax,
      mp?.contractValueIncludeMissingValue,
      openSidebar
    ]
  );

  return (
    <div className={styles.button}>
      <Button type={'text'} onClick={() => setModalVisible(true)}>
        {t('Tenders.copyMp')}
      </Button>
      {modalVisible && (
        <CopyMpModal onSubmit={onSubmit} onCancel={() => setModalVisible(false)} initialValues={initialValues} />
      )}
    </div>
  );
};

export default CopyMatchingProfile;
